// ─── editableTemplates3.ts ───────────────────────────────────────────────────
// Templates: 9 = Expense Report, 10 = Performance Review,
//            11 = Sales Invoice,  12 = Meeting Agenda

import { fe, fbe, esc, wrapDoc } from './editableTemplates';

const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// ─── Template 9: Expense Report ───────────────────────────────────────────

const expenseCalcScript = `<script>
function fmtMoney(n){return '$'+parseFloat(n||0).toFixed(2);}
function calcExpense(){
  var total=0;
  document.querySelectorAll('.exp-row').forEach(function(row){
    var amtEl=row.querySelector('.exp-amt');
    var amt=parseFloat((amtEl?amtEl.textContent:'0').replace(/[^0-9.]/g,''))||0;
    total+=amt;
  });
  var t=document.getElementById('f_grand_total');
  if(t) t.textContent=fmtMoney(total);
}
document.querySelectorAll('.exp-amt').forEach(function(el){el.addEventListener('input',calcExpense);});
calcExpense();
</script>`;

const expenseBody = (d: Record<string, string>, ed: boolean) => {
  const rows = [1,2,3,4,5,6].map(i => {
    const date = d[`exp${i}_date`] || '';
    const cat  = d[`exp${i}_cat`]  || '';
    const desc = d[`exp${i}_desc`] || '';
    const amt  = d[`exp${i}_amt`]  || '';
    return ed
      ? `<tr class="exp-row">
          <td class="e" contenteditable="true" id="f_exp${i}_date" data-ph="MM/DD/YYYY">${date}</td>
          <td class="e" contenteditable="true" id="f_exp${i}_cat"  data-ph="Category">${cat}</td>
          <td class="e" contenteditable="true" id="f_exp${i}_desc" data-ph="Description">${desc}</td>
          <td class="e exp-amt" contenteditable="true" id="f_exp${i}_amt" data-ph="0.00">${amt}</td>
        </tr>`
      : (desc || date || amt
          ? `<tr><td>${esc(date)}</td><td>${esc(cat)}</td><td>${esc(desc)}</td><td>${esc(amt)}</td></tr>`
          : '');
  }).join('');

  return `
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:26px;padding-bottom:18px;border-bottom:3px solid #1a1a1a">
  <div>
    <div style="font-size:26px;font-weight:900;letter-spacing:-1px">EXPENSE REPORT</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Report Date</div>
    <div style="font-weight:700">${fe('report_date', today, d.report_date || today, ed)}</div>
  </div>
</div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px">
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Employee Name</div>
    <div style="font-weight:700">${fe('employee', 'Employee Name', d.employee || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Department</div>
    <div style="font-weight:700">${fe('department', 'Department', d.department || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Report Period</div>
    <div style="font-weight:700">${fe('period', 'Month YYYY', d.period || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Manager</div>
    <div style="font-weight:700">${fe('manager', 'Manager Name', d.manager || '', ed)}</div></div>
</div>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin-bottom:8px">Expenses</div>
<table>
  <tr>
    <th style="width:18%">Date</th>
    <th style="width:20%">Category</th>
    <th style="width:40%">Description</th>
    <th style="width:22%">Amount</th>
  </tr>
  ${rows}
  <tr style="background:#f3f4f6">
    <td colspan="3" style="text-align:right;font-weight:700">Total</td>
    <td style="font-weight:700;font-size:14px" id="f_grand_total">$0.00</td>
  </tr>
</table>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 10px">Business Purpose</div>
${fbe('purpose', 'Describe the business purpose for these expenses...', d.purpose || '', ed)}
<div style="display:flex;gap:40px;margin-top:36px">
  <div style="flex:1"><div style="border-top:1.5px solid #1a1a1a;padding-top:6px;font-size:11px;color:#6b7280;margin-top:30px">Employee Signature</div></div>
  <div style="flex:1"><div style="border-top:1.5px solid #1a1a1a;padding-top:6px;font-size:11px;color:#6b7280;margin-top:30px">Manager Approval</div></div>
  <div style="flex:1"><div style="border-top:1.5px solid #1a1a1a;padding-top:6px;font-size:11px;color:#6b7280;margin-top:30px">Date Approved</div></div>
</div>
${ed ? expenseCalcScript : ''}`;
};

export const buildEditableExpenseReport = (): string =>
  wrapDoc('Expense Report', expenseBody({}, true), true);
export const buildCleanExpenseReport = (d: Record<string, string>): string =>
  wrapDoc('Expense Report', expenseBody(d, false));

// ─── Template 10: Performance Review ──────────────────────────────────────

const ratingRow = (id: string, label: string, val: string, ed: boolean) => {
  const score = parseInt(val || '0', 10) || 0;
  const dots = [1,2,3,4,5].map(n =>
    `<span style="display:inline-block;width:14px;height:14px;border-radius:50%;background:${n<=score?'#1a1a1a':'#e5e7eb'};margin-right:3px"></span>`
  ).join('');
  return `<tr>
    <td style="font-weight:600;font-size:12px;width:40%">${label}</td>
    <td style="width:35%">${dots}</td>
    <td style="font-size:12px">${
      ed
        ? `<span class="e" contenteditable="true" id="f_${id}_score" data-ph="1–5">${val}</span> / 5`
        : `${esc(val||'—')} / 5`
    }</td>
  </tr>`;
};

const perfBody = (d: Record<string, string>, ed: boolean) => `
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:26px;padding-bottom:18px;border-bottom:3px solid #1a1a1a">
  <div>
    <div style="font-size:26px;font-weight:900;letter-spacing:-1px">PERFORMANCE REVIEW</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Review Date</div>
    <div style="font-weight:700">${fe('review_date', today, d.review_date || today, ed)}</div>
  </div>
</div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px">
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Employee Name</div>
    <div style="font-weight:700">${fe('employee', 'Employee Name', d.employee || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Department</div>
    <div style="font-weight:700">${fe('department', 'Department', d.department || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Reviewer</div>
    <div style="font-weight:700">${fe('reviewer', 'Reviewer Name', d.reviewer || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Review Period</div>
    <div style="font-weight:700">${fe('review_period', 'Jan – Dec 2025', d.review_period || '', ed)}</div></div>
</div>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin-bottom:8px">Performance Ratings <span style="font-size:11px;font-weight:400;color:#6b7280">(click score to edit)</span></div>
<table>
  <tr><th style="width:40%">Competency</th><th style="width:35%">Rating</th><th>Score</th></tr>
  ${ratingRow('quality', 'Quality of Work',          d.quality_score || '',          ed)}
  ${ratingRow('productivity', 'Productivity',         d.productivity_score || '',     ed)}
  ${ratingRow('communication', 'Communication',       d.communication_score || '',    ed)}
  ${ratingRow('teamwork', 'Teamwork & Collaboration', d.teamwork_score || '',         ed)}
  ${ratingRow('initiative', 'Initiative & Leadership',d.initiative_score || '',       ed)}
  ${ratingRow('reliability', 'Reliability & Punctuality', d.reliability_score || '', ed)}
</table>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Key Strengths</div>
${fbe('strengths', 'Describe the employee\'s key strengths and positive contributions...', d.strengths || '', ed)}
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Areas for Improvement</div>
${fbe('improvements', 'Describe areas where the employee can grow and improve...', d.improvements || '', ed)}
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Goals for Next Review Period</div>
${fbe('goals', 'List specific, measurable goals for the upcoming review period...', d.goals || '', ed)}
<div style="display:flex;gap:40px;margin-top:40px">
  <div style="flex:1"><div style="border-top:1.5px solid #1a1a1a;padding-top:6px;font-size:11px;color:#6b7280;margin-top:30px">Employee Signature</div></div>
  <div style="flex:1"><div style="border-top:1.5px solid #1a1a1a;padding-top:6px;font-size:11px;color:#6b7280;margin-top:30px">Reviewer Signature</div></div>
</div>`;

export const buildEditablePerformanceReview = (): string =>
  wrapDoc('Performance Review', perfBody({}, true), true);
export const buildCleanPerformanceReview = (d: Record<string, string>): string =>
  wrapDoc('Performance Review', perfBody(d, false));

// ─── Template 11: Sales Invoice ────────────────────────────────────────────

const salesInvCalcScript = `<script>
function fmtMoney(n){return '$'+parseFloat(n||0).toFixed(2);}
function calc(){
  var sub=0;
  document.querySelectorAll('.slr').forEach(function(row){
    var q=parseFloat((row.querySelector('.qty')?row.querySelector('.qty').textContent:'0').replace(/[^0-9.]/g,''))||0;
    var p=parseFloat((row.querySelector('.prc')?row.querySelector('.prc').textContent:'0').replace(/[^0-9.]/g,''))||0;
    var amt=q*p; sub+=amt;
    var ac=row.querySelector('.amt-cell'); if(ac) ac.textContent=fmtMoney(amt);
  });
  var taxEl=document.getElementById('f_tax_rate');
  var taxRate=parseFloat((taxEl?taxEl.textContent:'0').replace(/[^0-9.]/g,''))||0;
  var taxAmt=sub*taxRate/100;
  var s=document.getElementById('f_subtotal'); if(s) s.textContent=fmtMoney(sub);
  var td=document.getElementById('f_tax_display'); if(td) td.textContent=taxRate+'%';
  var ta=document.getElementById('f_tax_amount'); if(ta) ta.textContent=fmtMoney(taxAmt);
  var tl=document.getElementById('f_grand_total'); if(tl) tl.textContent=fmtMoney(sub+taxAmt);
}
document.querySelectorAll('.qty,.prc,#f_tax_rate').forEach(function(el){el.addEventListener('input',calc);});
calc();
</script>`;

const salesInvBody = (d: Record<string, string>, ed: boolean) => {
  const fmtMoney = (n: number) => '$' + n.toFixed(2);
  const rows: string[] = [];
  let sub = 0;
  for (let i = 1; i <= 5; i++) {
    const desc = d[`item${i}_desc`] || '';
    const qty  = parseFloat((d[`item${i}_qty`]  || '0').replace(/[^0-9.]/g,'')) || 0;
    const prc  = parseFloat((d[`item${i}_price`] || '0').replace(/[^0-9.]/g,'')) || 0;
    const amt  = qty * prc;
    sub += amt;
    rows.push(ed
      ? `<tr class="slr">
          <td class="e" contenteditable="true" id="f_item${i}_desc"  data-ph="Product or service">${esc(desc)}</td>
          <td class="e qty" contenteditable="true" id="f_item${i}_qty"   data-ph="1">${qty || ''}</td>
          <td class="e prc" contenteditable="true" id="f_item${i}_price" data-ph="0.00">${prc || ''}</td>
          <td class="amt-cell">${amt > 0 ? fmtMoney(amt) : '—'}</td>
        </tr>`
      : (desc ? `<tr><td>${esc(desc)}</td><td>${esc(d[`item${i}_qty`]||'')}</td><td>${fmtMoney(prc)}</td><td>${fmtMoney(amt)}</td></tr>` : '')
    );
  }
  const taxRate = parseFloat((d['tax_rate'] || '0').replace(/[^0-9.]/g,'')) || 0;
  const taxAmt  = sub * taxRate / 100;
  return `
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:26px;padding-bottom:18px;border-bottom:3px solid #1a1a1a">
  <div>
    <div style="font-size:32px;font-weight:900;letter-spacing:-1px">SALES INVOICE</div>
    <div style="font-size:11px;color:#6b7280;margin-top:5px">
      Invoice #: ${fe('invoice_num', 'SI-2026-001', d.invoice_num || '', ed)}
    </div>
  </div>
  <div style="text-align:right">
    <div style="font-size:15px;font-weight:700">${fe('company_name', 'Your Company Name', d.company_name || '', ed)}</div>
    <div style="font-size:11px;color:#6b7280;margin-top:4px;line-height:1.7">
      ${fe('company_address', '123 Main St, City, State ZIP', d.company_address || '', ed)}<br>
      ${fe('company_email', 'billing@yourcompany.com', d.company_email || '', ed)}<br>
      ${fe('company_phone', '(555) 000-0000', d.company_phone || '', ed)}
    </div>
  </div>
</div>
<div style="display:flex;gap:48px;margin-bottom:24px;flex-wrap:wrap">
  <div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Bill To</div>
    <div style="font-weight:700">${fe('client_name', 'Client Name', d.client_name || '', ed)}</div>
    <div style="font-size:11px;color:#6b7280;line-height:1.7">
      ${fe('client_address', 'Client Address', d.client_address || '', ed)}<br>
      ${fe('client_email', 'client@email.com', d.client_email || '', ed)}
    </div>
  </div>
  <div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Invoice Date</div>
    <div style="font-weight:700">${fe('invoice_date', today, d.invoice_date || today, ed)}</div>
  </div>
  <div>
    <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Due Date</div>
    <div style="font-weight:700">${fe('due_date', 'Net 30', d.due_date || '', ed)}</div>
  </div>
</div>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin-bottom:8px">Items</div>
<table>
  <tr>
    <th style="width:46%">Description</th>
    <th style="width:14%">Qty</th>
    <th style="width:18%">Unit Price</th>
    <th style="width:22%">Amount</th>
  </tr>
  ${rows.join('')}
  <tr><td colspan="3" style="text-align:right;color:#6b7280;font-size:11px">Subtotal</td>
    <td style="font-weight:600" id="f_subtotal">${ed ? '$0.00' : fmtMoney(sub)}</td></tr>
  <tr>
    <td colspan="2" style="text-align:right;color:#6b7280;font-size:11px">Tax Rate (%)</td>
    <td style="text-align:center">${ed ? `<span class="e" contenteditable="true" id="f_tax_rate" data-ph="0">0</span>%` : `${taxRate}%`}</td>
    <td style="color:#6b7280" id="f_tax_amount">${ed ? '$0.00' : fmtMoney(taxAmt)}</td>
  </tr>
  <tr style="background:#f3f4f6;font-weight:700">
    <td colspan="3" style="text-align:right">TOTAL DUE</td>
    <td style="font-size:14px" id="f_grand_total">${ed ? '$0.00' : fmtMoney(sub + taxAmt)}</td>
  </tr>
</table>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Payment Information</div>
<div style="background:#f9fafb;padding:13px 15px;border-left:3px solid #1a1a1a;">
  ${fbe('payment_info', 'Bank: Chase | Account: XXXX | Routing: XXXX | Zelle/Venmo: phone@email.com', d.payment_info || '', ed)}
</div>
<p style="font-size:11px;color:#9ca3af;margin-top:16px">Payment is due within 30 days. Thank you for your business!</p>
${ed ? salesInvCalcScript : ''}`;
};

export const buildEditableSalesInvoice = (): string =>
  wrapDoc('Sales Invoice', salesInvBody({}, true), true);
export const buildCleanSalesInvoice = (d: Record<string, string>): string =>
  wrapDoc('Sales Invoice', salesInvBody(d, false));

// ─── Template 12: Meeting Agenda ──────────────────────────────────────────

const agendaBody = (d: Record<string, string>, ed: boolean) => `
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:26px;padding-bottom:18px;border-bottom:3px solid #1a1a1a">
  <div>
    <div style="font-size:26px;font-weight:900;letter-spacing:-1px">MEETING AGENDA</div>
    <div style="font-size:15px;font-weight:700;margin-top:4px;color:#4b5563">${fe('meeting_title', 'Meeting Title', d.meeting_title || '', ed)}</div>
  </div>
</div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px">
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Date &amp; Time</div>
    <div style="font-weight:700">${fe('meeting_datetime', today + ' at 10:00 AM', d.meeting_datetime || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Location / Link</div>
    <div style="font-weight:700">${fe('location', 'Conference Room A / Zoom Link', d.location || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Facilitator</div>
    <div style="font-weight:700">${fe('facilitator', 'Facilitator Name', d.facilitator || '', ed)}</div></div>
  <div><div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:2px">Attendees</div>
    <div style="font-weight:700">${fe('attendees', 'Names or Team / Dept', d.attendees || '', ed)}</div></div>
</div>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin-bottom:8px">Agenda Items</div>
<table>
  <tr><th style="width:15%">Time</th><th style="width:55%">Topic</th><th style="width:30%">Owner</th></tr>
  ${[1,2,3,4,5,6].map(i => ed
    ? `<tr>
        <td class="e" contenteditable="true" id="f_item${i}_time"  data-ph="${i===1?'0:00':'0:00'} min">${d[`item${i}_time`]||''}</td>
        <td class="e" contenteditable="true" id="f_item${i}_topic" data-ph="Agenda item ${i}">${d[`item${i}_topic`]||''}</td>
        <td class="e" contenteditable="true" id="f_item${i}_owner" data-ph="Presenter / Owner">${d[`item${i}_owner`]||''}</td>
      </tr>`
    : (d[`item${i}_topic`]
        ? `<tr><td>${esc(d[`item${i}_time`]||'')}</td><td>${esc(d[`item${i}_topic`])}</td><td>${esc(d[`item${i}_owner`]||'')}</td></tr>`
        : '')
  ).join('')}
</table>
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Objectives / Pre-Read</div>
${fbe('objectives', 'List the goals of this meeting or any materials attendees should review beforehand...', d.objectives || '', ed)}
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Action Items from Last Meeting</div>
${fbe('action_items', 'Outstanding action items carried over from the previous meeting...', d.action_items || '', ed)}
<div style="font-size:14px;font-weight:700;border-bottom:2px solid #1a1a1a;padding-bottom:5px;margin:20px 0 8px">Notes</div>
${fbe('notes', 'Space for meeting notes and decisions made...', d.notes || '', ed)}
<p style="font-size:11px;color:#9ca3af;margin-top:20px">Please review the agenda before the meeting. Send updates to the facilitator at least 24 hours in advance.</p>`;

export const buildEditableMeetingAgenda = (): string =>
  wrapDoc('Meeting Agenda', agendaBody({}, true), true);
export const buildCleanMeetingAgenda = (d: Record<string, string>): string =>
  wrapDoc('Meeting Agenda', agendaBody(d, false));
